<template>
  <div data-page-name="product">
    <section class="hero">
      <div class="wrapper">
        <div class="left">
          <h1>
            {{ $t('Meet') }}
            <SvgIcon name="ootbi" />
          </h1>

          <SvgIcon name="best-storage-for-veeam" class="slogan" />
          <p>{{ $t('object-storage.hero.description') }}</p>

          <Buttons.Link
            :to="localePathWithSlash(PATHS.REQUEST_DEMO.path)"
            accent="orange"
          >
            {{ $t('Request_a_Demo') }}
            <SvgIcon name="arrow-forward" />
          </Buttons.Link>
        </div>

        <div class="right">
          <div class="video-frame">
            <AppVideo.Preview
              :visible="isPreviewVisible"
              :duration="126"
              :alt="$t('object-storage.hero.video.alt')"
              :src="getMuxPreview(playbackId)"
              @play="playHandler"
            />

            <AppVideo.Player
              ref="video"
              :visible="!isPreviewVisible"
              :viewer-id
              :playback-id="playbackId"
              video-title="Ootbi - Old Sizes"
            />
          </div>
        </div>
      </div>
    </section>

    <section id="whats-new-with-ootbi">
      <div class="wrapper">
        <h2 v-html="$t('object-storage.whats-new-with-ootbi.h2')" />
        <h3 v-html="$t('object-storage.whats-new-with-ootbi.h3')" />

        <ul class="cards">
          <li v-for="index of 2" :key="index">
            <div class="card">
              <strong
                v-html="
                  $t(
                    `object-storage.whats-new-with-ootbi.models-${index}.title`,
                  )
                "
              />

              <span
                v-html="
                  $t(`object-storage.whats-new-with-ootbi.models-${index}.desc`)
                "
              />
            </div>

            <span
              v-html="
                index === 2 ? $t('AcquisitionModels.table.row-5') : '&nbsp;'
              "
              :class="['available-in-usa', { 'mobile-hidden': index !== 2 }]"
            />
          </li>
        </ul>

        <div class="cta">
          <div class="left">
            <p v-html="$t('object-storage.whats-new-with-ootbi.cta.desc')" />
          </div>

          <div class="right">
            <Buttons.Link
              :to="localePathWithSlash(PATHS.CONSUMPTION_MODEL.path)"
              target="_blank"
            >
              {{ $t('Learn_more') }}
              <SvgIcon name="arrow-forward" />
            </Buttons.Link>
          </div>
        </div>
      </div>
    </section>

    <section class="immutability-for-everyone">
      <div class="wrapper">
        <h3 v-html="$t('object-storage.immutability-for-everyone.h3')" />
        <p v-html="$t('object-storage.immutability-for-everyone.p')" />
        <b v-html="$t('object-storage.immutability-for-everyone.b')" />

        <figure>
          <figcaption>
            <header>
              <h4
                v-html="
                  $t('object-storage.immutability-for-everyone.figure.h4')
                "
              />
              <strong
                v-html="
                  $t('object-storage.immutability-for-everyone.figure.strong')
                "
              />
            </header>

            <NuxtImg
              v-photo-swipe="{
                caption: isTouchDevice
                  ? $t('ArticleMarkup.zoom-caption-pinch')
                  : $t('ArticleMarkup.zoom-caption-scroll', {
                      icon: SvgZoomIn,
                    }),
              }"
              :alt="$t('object-storage.immutability-for-everyone.figure.alt')"
              src="/redesign/images/SOBR-capacity-breakdown.webp"
              sizes="sm:704px md:820px"
              densities="x1 x2"
            />

            <footer>
              <p
                v-html="$t('object-storage.immutability-for-everyone.figure.p')"
              />
            </footer>
          </figcaption>
        </figure>

        <ul>
          <Cards.IconBullets
            v-for="(item, key) in WHATS_NEW"
            :key="key"
            v-bind="item"
          />
        </ul>
      </div>
    </section>

    <section class="secure">
      <div class="wrapper">
        <div class="content">
          <h3 v-html="$t('object-storage.secure.title')" />

          <ul>
            <li>
              <p v-html="$t('object-storage.secure-1')" />
            </li>

            <li>
              <p
                v-html="
                  $t('object-storage.secure-2', {
                    link: localePathWithSlash(PATHS.TECHNICAL_PAPERS.path),
                  })
                "
              />
            </li>

            <li>
              <p v-html="$t('object-storage.secure-3')" />
            </li>
          </ul>
        </div>

        <NuxtImg
          :alt="$t('Alts.Background.secure.lock-sphere')"
          src="/redesign/images/lock-and-purple-sphere-desktop.png"
          width="384"
          height="608"
          class="desktop"
        />

        <NuxtImg
          :alt="$t('Alts.Background.secure.lock-sphere')"
          src="/redesign/images/lock-and-purple-sphere-tablet.png"
          width="249"
          height="667"
          class="tablet"
        />
      </div>
    </section>

    <section class="simple">
      <div class="wrapper">
        <div class="content">
          <h3 v-html="$t('object-storage.simple.title')" />

          <ul>
            <li>
              <p v-html="$t('object-storage.simple-1')" />
            </li>

            <li>
              <p v-html="$t('object-storage.simple-2')" />
            </li>

            <li>
              <p v-html="$t('object-storage.simple-3')" />
            </li>
          </ul>
        </div>

        <NuxtImg
          :alt="$t('Alts.Background.simple.cubes')"
          src="/redesign/images/cubes-desktop.png"
          width="384"
          height="608"
          class="desktop"
        />

        <NuxtImg
          :alt="$t('Alts.Background.simple.cubes')"
          src="/redesign/images/cubes-tablet.png"
          width="249"
          height="640"
          class="tablet"
        />
      </div>
    </section>

    <section class="powerful">
      <div class="wrapper">
        <div class="content">
          <h3 v-html="$t('object-storage.powerful.title')" />

          <ul>
            <li>
              <p v-html="$t('object-storage.powerful-1')" />
            </li>

            <li>
              <p v-html="$t('object-storage.powerful-2')" />
            </li>

            <li>
              <p v-html="$t('object-storage.powerful-3')" />
            </li>
          </ul>
        </div>

        <!-- todo: alt -->
        <NuxtImg
          :alt="$t('Alts.Background.powerful.disks')"
          src="/redesign/images/storage-desktop.png"
          width="384"
          height="554"
          class="desktop"
        />

        <!-- todo: alt -->
        <NuxtImg
          :alt="$t('Alts.Background.powerful.disks')"
          src="/redesign/images/storage-tablet.png"
          width="249"
          height="586"
          class="tablet"
        />
      </div>
    </section>

    <section class="architecture">
      <div class="wrapper">
        <h3 v-html="$t('object-storage.architecture.title')" />

        <p v-html="$t('object-storage.architecture.description')" />
        <strong v-html="$t('object-storage.architecture.description-2')" />
        <p v-html="$t('object-storage.architecture.description-3')" />

        <div class="image-wrapper">
          <NuxtImg
            :alt="$t('object-storage.architecture.scheme-alt')"
            src="/redesign/images/ztdr-architecture.webp"
            width="1216"
            height="663"
            densities="x1 x2"
            format="webp"
          />
        </div>
      </div>
    </section>

    <section class="box-to-backup">
      <div class="wrapper">
        <div class="col-1">
          <h3 v-html="$t('object-storage.box-to-backup.title')" />
          <p v-html="$t('object-storage.box-to-backup.description')" />
        </div>

        <div class="col-2">
          <ExpandableList :items="unboxing">
            <template #heading="{ item }">
              <h5>{{ item.title }}</h5>
            </template>

            <template #content="{ item: { description, videoLink, title } }">
              <p>{{ description }}</p>

              <YTEmbed :src="videoLink" :title />
            </template>
          </ExpandableList>
        </div>
      </div>
    </section>

    <section class="why-object-storage">
      <div class="wrapper">
        <h3 v-html="$t('object-storage.why-object-storage.title')" />

        <div class="row">
          <p v-html="$t('object-storage.why-object-storage.description')" />

          <NuxtImg
            :alt="$t('Alts.Server.orange-front')"
            src="/redesign/images/server/server-front.png"
            width="592"
            height="111"
          />
        </div>

        <h4 v-html="$t('object-storage.why-object-storage.subtitle')" />

        <ul>
          <li v-for="{ icon, title, description } of benefits" :key="title">
            <h5>{{ title }}</h5>
            <SvgIcon :name="icon" />
            <p>{{ description }}</p>
          </li>
        </ul>
      </div>
    </section>

    <section class="get-demo-now">
      <NuxtImg
        :alt="$t('Alts.Background.purple-cube')"
        src="/redesign/images/figures/purple-cube.webp"
        densities="x1 x2"
        format="webp"
        loading="lazy"
        sizes="xs:1188px sm:1676px"
        class="figure"
      />

      <div class="wrapper">
        <div class="left">
          <div class="top">
            <h3>{{ $t('object-storage.get-demo-now.left.top.h3') }}</h3>
            <p>{{ $t('object-storage.get-demo-now.left.top.p') }}</p>
          </div>

          <div class="bottom">
            <strong>
              {{ $t('object-storage.get-demo-now.left.bottom.strong') }}
            </strong>

            <ul>
              <li>
                <p>
                  {{ $t('object-storage.get-demo-now.left.bottom.ul.li-1.p') }}
                </p>
              </li>

              <li>
                <p>
                  {{ $t('object-storage.get-demo-now.left.bottom.ul.li-2.p') }}
                </p>
              </li>

              <li>
                <p>
                  {{ $t('object-storage.get-demo-now.left.bottom.ul.li-3.p') }}
                </p>
              </li>
            </ul>
          </div>
        </div>

        <div class="right">
          <Forms.Wrapper>
            <strong class="title">
              {{ $t('object-storage.get-demo-now.right.strong') }}
            </strong>

            <Forms.RequestDemo
              :btn-text="$t('Submit')"
              product="Object Storage"
              page="object-storage"
              gtm-event="demo"
              accent="purple"
              @loading="setLoading"
              @submitted="setFormSubmitted(true)"
            />

            <transition appear name="fade">
              <Modals.SeeYouSoon v-if="isFormSubmitted" accent="purple" />
            </transition>
          </Forms.Wrapper>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
// components
import Cards from '~/components/redesign/Cards';
import Forms from '~/components/redesign/Forms';
import Modals from '~/components/redesign/Modals';
import Buttons from '~/components/redesign/Buttons';
import AppVideo from '~/components/redesign/AppVideo';
import ExpandableList from '~/components/ExpandableList.vue';
import YTEmbed from '~/components/redesign/YTEmbed.vue';

// composable
import { useI18n } from '#i18n';
import useApiSeo from '~/composables/useApiSeo';
import useLocalePathWithSlash from '~/composables/useLocalePathWithSlash';

// stores
import useUIStore from '~/stores/ui';

// utils
import getMuxPreview from '~/utils/getMuxPreview';

// services
import { UserId } from '~/services';

// constants
import { NuxtImg } from '#components';
import { VIDEOS, PATHS } from '~/constants';

// directives
import vPhotoSwipe from '~/directives/photoswipe';

// icons
import SvgZoomIn from '~/assets/svg/zoom-in.svg?raw';

const { t } = useI18n();
const { setLoading } = useUIStore();
const localePathWithSlash = useLocalePathWithSlash();

const unboxing = [
  {
    title: t('object-storage.box-to-backup.accordion-1.title'),
    description: t('object-storage.box-to-backup.accordion-1.description'),
    videoLink: 'https://www.youtube.com/embed/YuaOcLp-wdc?si=8voToCH9-2oR40EG',
  },
  {
    title: t('object-storage.box-to-backup.accordion-2.title'),
    description: t('object-storage.box-to-backup.accordion-2.description'),
    videoLink: 'https://www.youtube.com/embed/5N3uVNAUMXc?si=aXBGLU4Bxhrs-ymY',
  },
  {
    title: t('object-storage.box-to-backup.accordion-3.title'),
    description: t('object-storage.box-to-backup.accordion-3.description'),
    videoLink: 'https://www.youtube.com/embed/e7dPNwTJPok?si=fy3AVcKSdPsdLGNt',
  },
  {
    title: t('object-storage.box-to-backup.accordion-4.title'),
    description: t('object-storage.box-to-backup.accordion-4.description'),
    videoLink: 'https://www.youtube.com/embed/uWDYPpK8vUg?si=WiB3g-hVW1iX7MIM',
  },
  {
    title: t('object-storage.box-to-backup.accordion-5.title'),
    description: t('object-storage.box-to-backup.accordion-5.description'),
    videoLink: 'https://www.youtube.com/embed/QhIsCom72TI?si=bAO2isanjtUpoAFW',
  },
];

const benefits = [
  {
    icon: 'immutability-colorfull',
    title: t('object-storage.why-object-storage.immutability.title'),
    description: t(
      'object-storage.why-object-storage.immutability.description',
    ),
  },
  {
    icon: 'scalability-colorfull',
    title: t('object-storage.why-object-storage.scalability.title'),
    description: t('object-storage.why-object-storage.scalability.description'),
  },
  {
    icon: 'up-to-date-colorfull',
    title: t('object-storage.why-object-storage.up_to_date.title'),
    description: t('object-storage.why-object-storage.up_to_date.description'),
  },
  {
    icon: 'on-perm-to-cloud-colorfull',
    title: t('object-storage.why-object-storage.on_prem_to_cloud.title'),
    description: t(
      'object-storage.why-object-storage.on_prem_to_cloud.description',
    ),
  },
  {
    icon: 'reliability-colorfull',
    title: t('object-storage.why-object-storage.reliability.title'),
    description: t('object-storage.why-object-storage.reliability.description'),
  },
  {
    icon: 'availability-colorfull',
    title: t('object-storage.why-object-storage.availability.title'),
    description: t(
      'object-storage.why-object-storage.availability.description',
    ),
  },
];

const WHATS_NEW = [
  {
    title: t('object-storage.immutability-for-everyone.card-1.title'),
    icon: 'file-filled-black',
    bullets: [
      t('object-storage.immutability-for-everyone.card-1.bullet-1'),
      t('object-storage.immutability-for-everyone.card-1.bullet-2'),
      t('object-storage.immutability-for-everyone.card-1.bullet-3'),
    ],
  },
  {
    title: t('object-storage.immutability-for-everyone.card-2.title'),
    icon: 'rocket-colorfull',
    bullets: [
      t('object-storage.immutability-for-everyone.card-2.bullet-1'),
      t('object-storage.immutability-for-everyone.card-2.bullet-2'),
      t('object-storage.immutability-for-everyone.card-2.bullet-3'),
    ],
  },
  {
    title: t('object-storage.immutability-for-everyone.card-3.title'),
    icon: 'diamond-colorfull',
    bullets: [t('object-storage.immutability-for-everyone.card-3.bullet-1')],
  },
];

const isFormSubmitted = ref(false);
const setFormSubmitted = (value: boolean) => {
  isFormSubmitted.value = value;
};

const viewerId = ref<string>();

onMounted(() => {
  viewerId.value = UserId.get();
});

const isPreviewVisible = ref(true);
const videoRef = useTemplateRef('video');

const playHandler = () => {
  if (!videoRef.value) return;

  isPreviewVisible.value = false;
  videoRef.value.play();
};

const {
  public: { env },
} = useRuntimeConfig();

const playbackId = useState('playback-id', () =>
  env === 'production'
    ? VIDEOS.OOTBI_OLD_SIZES_PROD
    : VIDEOS.OOTBI_OLD_SIZES_DEV,
);

const isTouchDevice = computed(
  () => import.meta.client && 'ontouchstart' in document,
);

useApiSeo('object-storage', 'Page', {
  title: t('object-storage.seo.title'),
  description: t('object-storage.seo.description'),
  ogType: 'website',
});
</script>

<style scoped lang="scss">
@use '_/mixins/text-transform';
@use '_/mixins/position';
@use '_/mixins/size';
@use '_/mixins/media';
@use '_/mixins/typo';
@use '_/mixins/flex';
@use '_/fn';
@use '_/variables/color';
@use '_/variables/shadow';

[data-page-name='product'] {
  section.hero {
    margin-bottom: 2rem;

    .wrapper {
      padding: 8rem 2rem 7.54rem;

      @include size.wrapper(80rem);
      @include flex.center-sb;

      gap: 2rem;

      @include media.tablet {
        flex-direction: column;
        padding: 5rem 2rem 2.69rem;
      }

      @include media.mobile {
        gap: 1.5rem;
        padding: 2.5rem 1rem 3.04rem;
      }

      .left,
      .right {
        width: 100%;
      }

      .left {
        @include flex.start-start;

        flex-direction: column;
        gap: 2rem;

        @include media.tablet {
          gap: 1.5rem;
        }

        h1 {
          @include typo.h3;

          @include media.mobile {
            @include typo.h1;
          }

          svg {
            display: inline-block;
            width: auto;

            @include size.fixed-height(2rem);

            @include media.tablet {
              @include size.fixed-height(1.7rem);
            }

            @include media.mobile {
              @include size.fixed-height(1.8rem);

              margin-left: 0.5rem;
            }
          }
        }

        svg.slogan {
          @include size.fluid-max-width(30.3rem);

          height: auto;
          fill: fn.token('text-primary');

          @include media.tablet {
            @include size.fluid-max-width(28.8rem);
          }

          @include media.mobile {
            @include size.fluid-max-width(11.5rem);
          }
        }

        p {
          @include typo.body-1;
        }

        [data-component-name='Buttons.Link'] {
          @include media.mobile {
            width: 100%;
          }
        }
      }

      .right {
        .video-frame {
          position: relative;
          aspect-ratio: 16 / 9;
          z-index: 0;

          [data-component-name='AppVideo.Preview'] {
            max-height: calc(100% - 4px);
          }
        }
      }
    }
  }

  section#whats-new-with-ootbi {
    margin-bottom: 0;

    .wrapper {
      @include size.wrapper(80rem);

      padding: 2rem;

      @include flex.start-start;

      flex-direction: column;

      @include media.mobile {
        padding: 1rem;
      }

      h2 {
        @include typo.h2;

        align-self: center;
        margin-bottom: 4rem;

        :deep(span.red) {
          color: fn.token('error');
        }

        @include media.tablet {
          margin-bottom: 1.5rem;
        }

        @include media.mobile {
          text-align: center;
          margin-bottom: 2rem;
        }
      }

      h3 {
        @include typo.h3;

        text-align: start;
        margin-bottom: 2rem;
      }

      ul.cards {
        width: 100%;

        @include flex.stretch-sb;

        gap: 2rem;
        margin-bottom: 2rem;

        @include media.mobile {
          flex-direction: column;
          gap: 1rem;
        }

        li {
          width: 50%;
          display: flex;
          flex-direction: column;

          @include media.mobile {
            width: 100%;
          }

          .card {
            padding: 2rem;
            background-color: fn.token('surf-cont-secondary');
            display: flex;
            flex-direction: column;
            height: 100%;

            @include media.mobile {
              min-height: 10.0625rem;
            }

            strong {
              @include typo.h4;

              display: block;
              margin-bottom: 1.5rem;
            }

            span {
              @include typo.subtitle-2;
            }

            @include media.mobile {
              padding: 1rem;
            }
          }

          span.available-in-usa {
            @include typo.caption;

            margin-top: 1rem;
            display: block;
            height: 2.25rem;
          }

          span.mobile-hidden {
            @include media.mobile {
              display: none;
            }
          }
        }
      }

      .cta {
        width: 100%;

        @include flex.start-sb;

        gap: 0 2rem;

        @include media.tablet {
          flex-direction: column;
        }

        @include media.mobile {
          margin-bottom: 2.5rem;
        }

        .left {
          @include size.fluid-max-width(49rem);

          @include media.tablet {
            max-width: 100%;
          }

          @include media.mobile {
            margin-bottom: 0.5rem;
          }

          p {
            @include typo.body-2;

            margin-bottom: 1rem;
          }

          strong {
            @include typo.subtitle-2;
          }
        }

        .right {
          @include media.tablet {
            width: 100%;
          }

          [data-component-name='Buttons.Link'] {
            display: inline-block;
            min-width: 16.25rem;

            @include media.tablet {
              float: right;
            }

            @include media.mobile {
              display: block;
              width: 100%;
              margin-bottom: 0.5rem;
            }
          }
        }
      }
    }
  }

  section.immutability-for-everyone {
    margin-bottom: 7.25rem;

    .wrapper {
      @include size.wrapper(80rem);

      padding: 2rem;

      @include flex.start-start;

      flex-direction: column;

      @include media.mobile {
        padding: 1rem;
      }

      h3 {
        @include typo.h3;

        margin-bottom: 2rem;

        @include media.mobile {
          margin-bottom: 2rem;
        }
      }

      p {
        @include typo.body-2;
      }

      b {
        @include typo.subtitle-2;
      }

      figure {
        @include size.wrapper(50.0313rem);

        margin: 4.5rem auto;

        @include media.tablet {
          margin: 3rem auto 4rem;
        }

        @include media.mobile {
          margin: 2.5rem auto;
        }

        figcaption {
          h4 {
            @include typo.h3;

            margin-bottom: 1rem;

            @include media.mobile {
              margin-bottom: 0.5rem;
            }

            :deep(span) {
              background-color: color.$green;
              color: fn.token('text-reversed');
              padding-inline: 0.5rem;
            }
          }

          :deep(strong) {
            @include typo.h4;

            sup {
              font-size: 1.125rem;

              @include media.mobile {
                font-size: 0.5rem;
              }
            }
          }

          img {
            width: 100%;
            height: auto;
          }

          :deep(p) {
            @include typo.body-4;

            sup {
              font-size: 0.625rem;
            }
          }
        }
      }

      ul {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 2rem;

        @include media.mobile {
          grid-template-columns: 1fr;
          gap: 0;
        }
      }
    }
  }

  section.architecture {
    margin-bottom: 10.25rem;

    @include media.tablet {
      margin-bottom: 5.3rem;
    }

    @include media.mobile {
      margin-bottom: 4.6rem;
    }

    .wrapper {
      @include size.wrapper(80rem);

      padding: 0 2rem;

      @include flex.center-start;

      flex-direction: column;

      @include media.mobile {
        padding: 0 1rem;
      }

      h3 {
        @include typo.h3;

        text-align: center;
        margin-bottom: 2rem;

        @include media.mobile {
          text-align: left;
          margin-bottom: 1rem;
        }
      }

      p,
      strong {
        @include size.wrapper(50rem);

        @include media.tablet {
          text-align: center;
        }

        @include media.mobile {
          text-align: left;
          margin-bottom: 1.5rem;
        }
      }

      p {
        @include typo.body-2;

        margin-bottom: 2rem;
      }

      strong {
        @include typo.subtitle-2;

        margin-bottom: 1rem;
      }

      .image-wrapper {
        max-width: 100%;
        overflow-x: auto;

        @include media.mobile {
          margin: 0 -1rem;
        }

        img {
          width: 100%;
          height: auto;
          margin-top: 3rem;

          @include media.tablet {
            margin-top: 2rem;
          }

          @include media.mobile {
            margin-top: 1.5rem;
            height: 20.6rem;
            width: auto;
          }
        }
      }
    }
  }

  section.secure,
  section.simple,
  section.powerful {
    margin-bottom: 8rem;
    padding: 0 2rem;

    @include media.tablet {
      margin-bottom: 3.8rem;
    }

    @include media.mobile {
      padding: 0 1rem;
      margin-bottom: 1.5rem;
    }

    .wrapper {
      @include flex.center-sb;
      @include size.wrapper(76rem);

      box-shadow: shadow.$elevation-3;
      gap: 2rem;

      .content {
        padding: 5rem 0 5rem 5rem;

        @include media.tablet {
          padding: 2.5rem 0 2.5rem 2.5rem;
        }

        @include media.mobile {
          padding: 1.5rem 1rem;
        }

        h3 {
          @include typo.h3;

          margin-bottom: 2rem;

          @include media.tablet {
            margin-bottom: 1.5rem;
          }
        }

        ul {
          li {
            border-top: 1px solid fn.token('outline-primary');
            padding: 1.5rem 0;

            @include media.mobile {
              padding: 1rem 0;
            }

            p {
              @include typo.body-2;

              :deep(a) {
                @include typo.link-text-2;
              }

              @include media.mobile {
                line-height: 1.55rem;
              }
            }
          }
        }
      }

      img.desktop {
        @include media.tablet {
          display: none;
        }
      }

      img.tablet {
        display: none;

        @include media.tablet {
          display: block;
        }

        @include media.mobile {
          display: none;
        }
      }
    }
  }

  section.powerful {
    margin-bottom: 10.25rem;

    .wrapper {
      img.desktop {
        align-self: end;
      }
    }

    @include media.tablet {
      margin-bottom: 6rem;
    }

    @include media.mobile {
      margin-bottom: 4.2rem;
    }
  }

  section.box-to-backup {
    margin-bottom: 10.25rem;

    @include media.tablet {
      margin-bottom: 8rem;
    }

    @include media.mobile {
      margin-bottom: 4.8rem;
    }

    .wrapper {
      @include size.wrapper(80rem);
      @include flex.start-sb;

      gap: 2rem;
      padding: 0 2rem;

      @include media.tablet {
        flex-direction: column;
      }

      @include media.mobile {
        padding: 0 1rem;
      }

      .col-1,
      .col-2 {
        width: 100%;
      }

      .col-1 {
        h3 {
          @include typo.h3;

          margin-bottom: 2rem;
          max-width: 63%;

          @include media.tablet {
            max-width: 100%;
          }

          @include media.mobile {
            font-size: 2.25rem;
            line-height: 2.4rem;
          }
        }

        p {
          @include typo.body-2;

          &:first-of-type {
            margin-bottom: 0.5rem;
          }
        }
      }

      .col-2 {
        [data-component-name='ExpandableList'] {
          h5 {
            @include typo.subtitle-1;
          }

          p {
            @include typo.body-2;
            margin-bottom: 2rem;
          }

          [data-component-name='YTEmbed'] {
            margin-bottom: 1.5rem;
          }
        }
      }
    }
  }

  section.why-object-storage {
    margin-bottom: 10.25rem;

    @include media.tablet {
      margin-bottom: 8rem;
    }

    @include media.mobile {
      margin-bottom: 5rem;
    }

    .wrapper {
      @include size.wrapper(80rem);

      padding: 0 2rem;

      @include media.mobile {
        padding: 0 1rem;
      }

      h3 {
        @include size.fluid-max-width(37rem);
        @include typo.h3;

        margin-bottom: 2rem;

        @include media.tablet {
          margin-bottom: 1.5rem;
          max-width: 100%;
        }

        @include media.mobile {
          margin-bottom: 1.5rem;
        }
      }

      .row {
        @include flex.start-sb;

        gap: 2rem;
        margin-bottom: 5rem;

        @include media.tablet {
          flex-direction: column;
          margin-bottom: 2.8rem;
        }

        @include media.mobile {
          margin-bottom: 2.5rem;
        }

        p {
          @include typo.body-2;
        }

        img {
          margin: -2rem auto 0;

          @include media.tablet {
            margin-top: -0.4rem;
          }

          @include media.mobile {
            width: 100%;
            height: auto;
          }
        }
      }

      :deep(h4) {
        @include typo.h4;
        @include text-transform.responsive-line-breaks;

        margin-bottom: 2rem;

        @include media.tablet {
          margin-bottom: 1.5rem;
        }
      }

      ul {
        display: grid;
        grid-template-rows: auto;
        grid-template-columns: repeat(3, 1fr);
        gap: 2rem;

        @include media.tablet {
          grid-template-columns: repeat(2, 1fr);
          gap: 1.5rem;
        }

        @include media.mobile {
          grid-template-columns: repeat(1, 1fr);
        }

        li {
          padding: 2rem;
          box-shadow: shadow.$elevation-1;

          @include media.tablet {
            padding: 1.5rem;
          }

          @include media.mobile {
            padding: 1.5rem 1rem;
          }

          h5 {
            @include typo.subtitle-1;

            margin-bottom: 1.5rem;
          }

          svg {
            margin-bottom: 1rem;

            @include media.mobile {
              margin-bottom: 1rem;
            }
          }

          p {
            @include typo.body-2;
          }
        }
      }
    }
  }

  section.get-demo-now {
    position: relative;
    overflow: hidden;
    padding: 8rem 0 8.51rem;
    background-color: fn.token('surf-cont-secon-action');

    @include media.tablet {
      padding: 5rem 0;
    }

    @include media.mobile {
      padding: 4rem 0 2.54rem;
    }

    img.figure {
      position: absolute;
      left: 50%;
      transform: translate(-76.4%, 11%);

      @include media.tablet {
        transform: translate(-76.4%, 12.3%);
      }

      @include media.mobile {
        transform: translate(-43%, 7%);
      }
    }

    .wrapper {
      position: relative;
      z-index: 0;
      padding: 0 2rem;

      @include size.wrapper(80rem);
      @include flex.stretch-sb;

      gap: 0 2rem;

      @include media.tablet {
        flex-direction: column;
        gap: 5rem 0;
      }

      @include media.mobile {
        padding: 0 1rem;
        gap: 4rem 0;
      }

      .left,
      .right {
        width: 100%;
      }

      .left {
        color: fn.token('text-reversed');

        .top {
          margin-bottom: 3rem;

          @include media.mobile {
            margin-bottom: 2rem;
          }

          h3 {
            @include typo.h3;

            margin-bottom: 1.5rem;

            @include media.mobile {
              margin-bottom: 1rem;
            }
          }

          p {
            @include typo.body-1;
          }
        }

        .bottom {
          strong {
            @include typo.subtitle-1;

            display: block;
            margin-bottom: 1.5rem;

            @include media.mobile {
              font-size: 1.5rem;
              line-height: 2.25rem;
              margin-bottom: 1rem;
            }
          }

          ul {
            list-style: disc;
            margin-left: 1.5rem;

            li {
              p {
                @include typo.body-2;
              }
            }
          }
        }
      }

      .right {
        @include media.tablet {
          @include size.wrapper(37rem);
        }

        [data-component-name='Forms.Wrapper'] {
          position: relative;

          strong.title {
            @include typo.h4;

            display: block;
            margin-bottom: 1.5rem;
          }

          [data-component-name='SeeYouSoon'] {
            @include position.absolute-full;

            z-index: 1;
            background-color: fn.token('surf-cont-secondary');
            padding: 2rem;

            @include media.mobile {
              padding: 1.5rem 1rem;
            }
          }

          @include media.mobile {
            margin-left: -1rem;
            width: calc(100% + 2rem);
          }
        }
      }
    }
  }
}
</style>
